import { HttpClient, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpXsrfTokenExtractor } from '@angular/common/http';
import { Injectable, InjectionToken, Inject, Injector } from '@angular/core';
import { BehaviorSubject, Observable, from, throwError } from 'rxjs';
import { catchError, filter, switchMap, take, timeout } from 'rxjs/operators';
import { Router } from '@angular/router';
import { RecaptchaService } from '../../../service/recaptcha/recaptcha.service';
import { AppConstants } from '../../constants/app-constants';
import { KnowledgeBaseConstants } from '../../constants/endpoints/knowledge-base/knowledge-base-constants';
import { environment } from 'src/environments/environment';
import { URL_ENDPOINT_PORTABILITY } from '../../constants/endpoints/portability/portability.constant';
import { SecurityAccessConstants } from '../../constants/endpoints/security-access/security-access-constants';
import { URL_ENDPOINT_BACKUP_CIFIN, URL_IDENTIFIER } from '../../constants/endpoints/cifin/cifin.constant';
import { BackendConstants } from '../../constants/endpoints/backend/backend.constants';

export const DEFAULT_TIMEOUT = new InjectionToken<number>('defaultTimeout');

@Injectable()
export class JwtHttpInterceptor implements HttpInterceptor {

    private isRefreshing = false;
    private refreshTokenSubject = new BehaviorSubject<string | null>(null);

    constructor(
        @Inject(DEFAULT_TIMEOUT) protected defaultTimeout: number,
        private tokenService: HttpXsrfTokenExtractor,
        private injector: Injector
    ) { }

    private get httpClient(): HttpClient {
        return this.injector.get(HttpClient);
    }

    private get router(): Router {
        return this.injector.get(Router);
    }

    private get recaptchaService(): RecaptchaService {
        return this.injector.get(RecaptchaService);
    }

    intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        const timeoutValue = Number(request.headers.get('timeout')) || this.defaultTimeout;
        let token = localStorage.getItem('access_token');
        let clone: HttpRequest<any>;

        if (request.url === AppConstants.URL_IP_ADDRESS || request.url.includes(environment.ApiGoogleMaps)
            || request.url === SecurityAccessConstants.URLSECURITYACCESS + '/users/key-services'
            || request.url.includes('/users/refresh-token')) {
            clone = request.clone({
                setHeaders: {
                }
            });
            return next.handle(clone);
        }

        if (request.url.includes(KnowledgeBaseConstants.ENDPOINT + '/searchquestion')) {
            token = sessionStorage.getItem('token_knowledge');
            clone = request.clone({
                setHeaders: {
                    Accept: `application/json`,
                    'Content-Type': `application/json`,
                    Authorization: `JWT ${token}`
                }
            });
            return next.handle(clone);
        }

        if (request.url.includes(URL_ENDPOINT_BACKUP_CIFIN) ||
            request.url.includes(URL_IDENTIFIER)) {
            const tokenCookie = localStorage.getItem('_hsvca');
            clone = request.clone({
                setHeaders: {
                    Accept: `application/json`,
                    'Content-Type': `application/json`,
                    'CSRF-Token': tokenCookie ? tokenCookie : `10df18ab-c98f-4958-8b31-d1e86b9f65e5`
                }
            });
            return next.handle(clone);
        }

        if (token) {
            if (!request.headers.has('Authorization')){
              //solo agregar token si el request no lo tiene
              clone = request.clone({
                  setHeaders: {
                      Accept: `application/json`,
                      'Content-Type': `application/json`,
                      Authorization: `Bearer ${token}`
                  }
              });
            }
            else {
              clone = request;
            }
        } else {
          if (
            request.url.includes(URL_ENDPOINT_PORTABILITY) ||
            request.url.includes(BackendConstants.URL_ENDPOINT_BACKEND) ||
            request.url.includes(BackendConstants.URL_ENDPOINT_BACKEND_SECURE)
            ) {
            clone = request.clone({
              setHeaders: {
                Accept: `application/json`,
                'Content-Type': `application/json`,
                Authorization: `Bearer ` + AppConstants.TOKEN_GENERICO
              }
            });
          } else {
            clone = request.clone({
              setHeaders: {
                Accept: `application/json`,
                'Content-Type': `application/json`
              }
            });
          }
        }

        return next.handle(clone).pipe(
            timeout(timeoutValue),
            catchError(error => {
                const refreshToken = localStorage.getItem('refresh_token');
                if (error.status === 401 && refreshToken) {
                    return this.handleRefresh(request, next, refreshToken);
                }
                return throwError(error);
            })
        );
    }

    private handleRefresh(request: HttpRequest<any>, next: HttpHandler, refreshToken: string): Observable<HttpEvent<any>> {
        if (this.isRefreshing) {
            return this.refreshTokenSubject.pipe(
                filter(token => token !== null),
                take(1),
                switchMap(newToken => next.handle(this.cloneWithToken(request, newToken)))
            );
        }

        this.isRefreshing = true;
        this.refreshTokenSubject.next(null);

        const refreshUrl = SecurityAccessConstants.SECURITY_ACCESS_ENDPOINT + '/users/refresh-token';
        return from(this.recaptchaService.executeRecaptcha(SecurityAccessConstants.RECAPTCHA_ACTION)).pipe(
            switchMap(recaptchaToken => {
                const body = {
                    refreshToken: refreshToken,
                    token: recaptchaToken,
                    recaptchaAction: SecurityAccessConstants.RECAPTCHA_ACTION
                };
                return this.httpClient.post<any>(refreshUrl, body);
            }),
            switchMap(response => {
                this.isRefreshing = false;
                const newToken = response && response.data ? response.data.token : null;
                if (newToken) {
                    localStorage.setItem('access_token', newToken);
                    const expiresIn = response.data.expiresIn ? response.data.expiresIn : 18000;
                    localStorage.setItem('token_expiry', (Date.now() + expiresIn * 1000).toString());
                    if (response.data.refreshToken) {
                        localStorage.setItem('refresh_token', response.data.refreshToken);
                    }
                    this.refreshTokenSubject.next(newToken);
                    return next.handle(this.cloneWithToken(request, newToken));
                }
                this.clearTokens();
                return throwError(response);
            }),
            catchError(err => {
                this.isRefreshing = false;
                this.clearTokens();
                this.router.navigate(['/ingreso']);
                return throwError(err);
            })
        );
    }

    private cloneWithToken(request: HttpRequest<any>, token: string): HttpRequest<any> {
        return request.clone({
            setHeaders: {
                Accept: `application/json`,
                'Content-Type': `application/json`,
                Authorization: `Bearer ${token}`
            }
        });
    }

    private clearTokens(): void {
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('token_expiry');
    }
}